import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  Grid,
  Box,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  CircularProgress,
  Badge,
  Stack,
  Divider,
  IconButton,
  CardActions
} from '@mui/material';
import {
  CardGiftcard as GiftIcon,
  Star as StarIcon,
  LocalShipping as ShippingIcon,
  Close as CloseIcon,
  ShoppingBag as ShopIcon,
  Assignment as OrderIcon,
  Add as AddIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
import { API_BASE_URL } from "../config/api";

// Cart helper functions with localStorage safety checks
const getCartItems = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return [];
  }
  try {
    const cartStr = localStorage.getItem("cart");
    if (!cartStr) return [];
    return JSON.parse(cartStr);
  } catch (error) {
    console.error("Error parsing cart data:", error);
    return [];
  }
};

const saveCartItems = (items) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  try {
    localStorage.setItem("cart", JSON.stringify(items));
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('cartUpdated'));
  } catch (error) {
    console.error("Error saving cart data:", error);
  }
};

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`giftshop-tabpanel-${index}`}
      aria-labelledby={`giftshop-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function GiftShop() {
  const [gifts, setGifts] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [selectedGift, setSelectedGift] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [couponHistory, setCouponHistory] = useState([]);
  const [activeTab, setActiveTab] = useState(0); // 0 for shop, 1 for history
  const [justPurchased, setJustPurchased] = useState(null); // Track recently added to cart
  const [quantity, setQuantity] = useState(1); // Gift quantity
  const navigate = useNavigate();

  // Check authentication
  useEffect(() => {
    // Check if we're in browser environment
    if (typeof window === 'undefined' || !window.localStorage) {
      setLoading(false);
      return;
    }

    try {
      const storedUser = localStorage.getItem("user");
      if (!storedUser) {
        navigate("/login", { replace: true });
        return;
      }

      // Parse stored user data
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);
      setLoading(false);
      
      // Fetch fresh data from backend
      fetchUserProfile();
    } catch (err) {
      console.error('Error parsing stored user data:', err);
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.removeItem('user');
      }
      navigate('/login');
    }
  }, [navigate]);

  // Fetch user profile including points
  const fetchUserProfile = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/profile.php`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) {
          setUser(data.user);
          // Update localStorage with fresh data
          if (typeof window !== 'undefined' && window.localStorage) {
            localStorage.setItem('user', JSON.stringify(data.user));
          }
        }
      }
    } catch (err) {
      console.error('Profile fetch error:', err);
    }
  };

  // Fetch gifts and order history
  useEffect(() => {
    const fetchData = async () => {
      if (!user || !user.name) return;
      
      try {
        // Fetch active gifts
        try {
          const giftsRes = await fetch(
            `${API_BASE_URL}/api/gift_shop.php?username=${encodeURIComponent(user.name)}`
          );
          const giftsData = await giftsRes.json();
          if (giftsData.success) {
            setGifts(Array.isArray(giftsData.data) ? giftsData.data : []);
          } else {
            setGifts([]);
            console.log("Gifts API error:", giftsData.error);
          }
        } catch (error) {
          console.log("Gifts fetch error:", error);
          setGifts([]);
        }

        // Fetch order history
        try {
          const ordersRes = await fetch(
            `${API_BASE_URL}/api/coupon_purchases.php?username=${encodeURIComponent(user.name)}`
          );
          const ordersData = await ordersRes.json();
          if (ordersData.success) {
            setCouponHistory(Array.isArray(ordersData.purchases) ? ordersData.purchases : []);
          } else {
            setCouponHistory([]);
          }
        } catch (error) {
          console.log("Coupon history fetch error:", error);
          setCouponHistory([]);
        }

      } catch (e) {
        showMessage("حدث خطأ أثناء تحميل متجر الهدايا", "error");
        setGifts([]);
        setCouponHistory([]);
      }
    };
    
    if (user) {
      fetchData();
    }
  }, [user]);

  const showMessage = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Purchase coupon directly (not added to cart)
  const purchaseCoupon = async (coupon) => {
    try {
      if (!user || !user.name) {
        showMessage("يرجى تسجيل الدخول أولاً", "error");
        return;
      }

      // Check if user has enough points
      if (!user.points || user.points < coupon.points_cost) {
        showMessage(`ليس لديك نقاط كافية. تحتاج إلى ${coupon.points_cost} نقطة ولديك ${user.points || 0} نقطة.`, "error");
        return;
      }

      // Create FormData for the purchase
      const formData = new FormData();
      formData.append('action', 'redeem');
      formData.append('gift_id', coupon.id);
      formData.append('item_type', 'coupon');
      formData.append('quantity', 1);
      formData.append('username', user.name);

      const response = await fetch(`${API_BASE_URL}/api/gift_shop.php`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        // Update user points
        const updatedUser = { ...user, points: data.remaining_points };
        setUser(updatedUser);
        if (typeof window !== 'undefined' && window.localStorage) {
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }

        // Show success with coupon code
        setJustPurchased({
          coupon: coupon,
          coupon_code: data.coupon_code,
          points_cost: data.points_cost,
          remaining_points: data.remaining_points
        });

        showMessage(`تم شراء الكوبون بنجاح! 🎉`, "success");
        setModalOpen(false);

        // Refresh gifts list to update availability
        const giftsRes = await fetch(
          `${API_BASE_URL}/api/gift_shop.php?username=${encodeURIComponent(user.name)}`
        );
        const giftsData = await giftsRes.json();
        if (giftsData.success) {
          setGifts(Array.isArray(giftsData.data) ? giftsData.data : []);
        }

        // Refresh coupon purchase history
        try {
          const historyRes = await fetch(
            `${API_BASE_URL}/api/coupon_purchases.php?username=${encodeURIComponent(user.name)}`
          );
          const historyData = await historyRes.json();
          if (historyData.success) {
            setCouponHistory(Array.isArray(historyData.purchases) ? historyData.purchases : []);
          }
        } catch (error) {
          console.log("Coupon history refresh error:", error);
        }
      } else {
        showMessage(data.error || "فشل شراء الكوبون", "error");
      }
    } catch (error) {
      console.error("Error purchasing coupon:", error);
      showMessage("حدث خطأ أثناء شراء الكوبون", "error");
    }
  };

  // Add gift to cart without immediate purchase
  const addGiftToCart = (gift, purchaseQuantity) => {
    try {
      if (!user || !user.name) {
        showMessage("يرجى تسجيل الدخول أولاً", "error");
        return;
      }

      // Check if user has enough points for this quantity
      const totalCost = gift.points_cost * purchaseQuantity;
      if (!user.points || user.points < totalCost) {
        showMessage(`ليس لديك نقاط كافية. تحتاج إلى ${totalCost} نقطة ولديك ${user.points || 0} نقطة.`, "error");
        return;
      }

      // Get current cart from localStorage
      const existingCart = getCartItems();
      let updatedCart = [...existingCart];

      // Add gift(s) to cart - each unit gets its own cart entry for unique tracking
      for (let i = 0; i < purchaseQuantity; i++) {
        const giftCartItem = {
          id: `gift_${gift.id}_${Date.now()}_${i}`, // Unique ID for each gift
          name: gift.name,
          description: gift.description,
          points_cost: gift.points_cost,
          image: gift.image,
          quantity: 1, // Always 1 per cart item for gifts
          type: 'gift', // Mark as gift item
          gift_id: gift.id, // Original gift ID
          gift_code: `pending_${Date.now()}_${i}` // Temporary code, will be generated on checkout
        };
        updatedCart.push(giftCartItem);
      }

      // Save to localStorage
      saveCartItems(updatedCart);

      // Dispatch cart update event
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('cartUpdated', { 
          detail: { cartItems: updatedCart, cartLength: updatedCart.length }
        }));
      }

      // Set just added for showing additional options
      setJustPurchased({
        gift: gift,
        quantity: purchaseQuantity,
        totalCost: totalCost,
        cartItems: updatedCart.slice(-purchaseQuantity) // Last added items
      });
      
      showMessage(`تم إضافة ${purchaseQuantity} من ${gift.name} إلى السلة! 🛒`, "success");
      
      setModalOpen(false);
      setQuantity(1); // Reset quantity

    } catch (error) {
      console.error("Error adding gift to cart:", error);
      showMessage("حدث خطأ أثناء إضافة الهدية للسلة", "error");
    }
  };

  const openModal = (gift) => {
    setSelectedGift(gift);
    setQuantity(1); // Reset quantity when opening modal
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedGift(null);
  };

  if (loading) {
      return (
        <Container maxWidth="lg" sx={{ mt: { xs: 10, md: 12 }, mb: 4, textAlign: 'center' }}>
          <Box sx={{ py: 8 }}>
            <CircularProgress size={48} sx={{ color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
              جاري التحميل...
            </Typography>
          </Box>
        </Container>
      );
    }

    return (
      <Container maxWidth="lg" sx={{ mt: { xs: 10, md: 12 }, mb: { xs: 10, md: 4 }, px: { xs: 2, md: 3 } }}>
        {/* User Points Display */}
        <Paper 
          elevation={0} 
          sx={{ 
            mb: 3, 
            p: 2.5,
            background: 'linear-gradient(135deg, #1677ff 0%, #4096ff 100%)',
            color: 'white',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Box>
            <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
              رصيدك من النقاط
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
              <StarIcon sx={{ fontSize: '2rem' }} />
              {user?.points || 0}
              <Typography component="span" variant="h6" sx={{ fontWeight: 500, opacity: 0.9 }}>
                نقطة
              </Typography>
            </Typography>
          </Box>
          <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              مرحباً، {user?.name}
            </Typography>
          </Box>
        </Paper>

        {/* Tabs */}
      <Paper elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              minHeight: '56px',
              fontSize: '1rem',
              fontWeight: 500,
              textTransform: 'none',
              color: 'text.secondary',
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 600
              }
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0'
            }
          }}
        >
          <Tab 
            icon={<ShopIcon sx={{ fontSize: '1.25rem' }} />} 
            label="متجر الهدايا" 
            iconPosition="start"
            sx={{ gap: 1 }}
          />
          <Tab 
            icon={
              <Badge badgeContent={couponHistory.length} color="primary" max={99}>
                <OrderIcon sx={{ fontSize: '1.25rem' }} />
              </Badge>
            } 
            label="تاريخ الكوبونات" 
            iconPosition="start"
            sx={{ gap: 1 }}
          />
        </Tabs>
      </Paper>

      {/* Gift Shop Tab */}
      <TabPanel value={activeTab} index={0}>
        {gifts.length === 0 ? (
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 4, md: 6 }, 
              textAlign: 'center',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'background.paper'
            }}
          >
            <GiftIcon sx={{ fontSize: { xs: 60, md: 80 }, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
              لا توجد هدايا متاحة حالياً
            </Typography>
            <Typography variant="body2" color="text.disabled">
              تحقق مرة أخرى لاحقاً للحصول على هدايا جديدة
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={{ xs: 1.5, md: 2 }}>
            {gifts.map((gift) => (
              <Grid item xs={6} sm={4} md={3} key={gift.id}>
                <Card 
                  elevation={0}
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 2,
                      borderColor: 'primary.light'
                    }
                  }}
                >
                  {gift.type === 'coupon' ? (
                    <Box
                      sx={{
                        height: 120,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'linear-gradient(135deg, #1677ff 0%, #4096ff 100%)',
                        color: 'white',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      {gift.already_purchased && (
                        <Chip
                          label="تم الشراء"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'success.main',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            zIndex: 2,
                            border: '2px solid white'
                          }}
                        />
                      )}
                      <Box sx={{ textAlign: 'center', zIndex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 0.5 }}>
                          كوبون خصم
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, fontSize: '1.5rem' }}>
                          {gift.discount_type === 'percent' 
                            ? `${gift.discount_value}٪` 
                            : `${gift.discount_value} د.ع`}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          position: 'absolute',
                          width: '200%',
                          height: '200%',
                          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
                          backgroundSize: '20px 20px',
                          transform: 'rotate(45deg)',
                          opacity: 0.3
                        }}
                      />
                    </Box>
                  ) : (
                    <CardMedia
                      component="img"
                      height="120"
                      image={gift.image ? `https://al-sadik.com/uploads/${gift.image}` : "/gift-placeholder.png"}
                      alt={gift.name}
                      sx={{ objectFit: 'cover' }}
                    />
                  )}
                  <CardContent sx={{ flexGrow: 1, p: 1.5 }}>
                    <Typography variant="subtitle2" component="h3" gutterBottom sx={{ 
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      lineHeight: 1.3,
                      mb: 0.5,
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 2,
                      overflow: 'hidden'
                    }}>
                      {gift.name}
                    </Typography>
                    {gift.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ 
                        mb: 1,
                        lineHeight: 1.4,
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 1,
                        overflow: 'hidden',
                        fontSize: '0.75rem'
                      }}>
                        {gift.description}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Chip
                        icon={<StarIcon sx={{ fontSize: '0.8rem' }} />}
                        label={`${gift.points_cost} نقطة`}
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          height: 24
                        }}
                      />
                    </Box>
                  </CardContent>
                  <CardActions sx={{ p: 1.5, pt: 0 }}>
                    {gift.type === 'coupon' && gift.already_purchased ? (
                      <Button
                        variant="outlined"
                        fullWidth
                        size="small"
                        disabled
                        sx={{
                          py: 0.75,
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          textTransform: 'none',
                          borderRadius: 1,
                          minHeight: 32,
                          borderColor: 'success.main',
                          color: 'success.main'
                        }}
                      >
                        ✓ تم الشراء مسبقاً
                      </Button>
                    ) : (
                      <Button
                        variant={user?.points >= gift.points_cost ? "contained" : "outlined"}
                        fullWidth
                        size="small"
                        disabled={!user?.points || user.points < gift.points_cost}
                        onClick={() => openModal(gift)}
                        sx={{
                          py: 0.75,
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          textTransform: 'none',
                          borderRadius: 1,
                          minHeight: 32,
                          ...(user?.points >= gift.points_cost && {
                            background: 'linear-gradient(135deg, #1677ff 0%, #4096ff 100%)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #0958d9 0%, #1677ff 100%)'
                            }
                          })
                        }}
                      >
                        {!user?.points || user.points < gift.points_cost ? 'نقاط غير كافية' : 'شراء'}
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      {/* Order History Tab */}
      <TabPanel value={activeTab} index={1}>
        {couponHistory.length === 0 ? (
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 4, md: 6 }, 
              textAlign: 'center',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'background.paper'
            }}
          >
            <OrderIcon sx={{ fontSize: { xs: 60, md: 80 }, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
              لا يوجد كوبونات مشتراة سابقة
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
              ابدأ بتصفح متجر الهدايا واستبدل نقاطك بكوبونات الخصم
            </Typography>
            <Button 
              variant="contained"
              onClick={() => setActiveTab(0)}
              startIcon={<ShopIcon />}
              sx={{
                py: 1.25,
                px: 3,
                fontWeight: 500,
                textTransform: 'none',
                borderRadius: 1.5
              }}
            >
              تصفح المتجر
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {couponHistory.map(purchase => (
              <Grid item xs={12} md={6} key={purchase.id}>
                <Card 
                  elevation={0}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      boxShadow: 1,
                      borderColor: 'primary.light'
                    }
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack direction="row" spacing={2}>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'linear-gradient(135deg, #1677ff 0%, #4096ff 100%)',
                          color: 'white',
                          borderRadius: 2,
                          position: 'relative',
                          overflow: 'hidden',
                          flexShrink: 0
                        }}
                      >
                        <Box sx={{ textAlign: 'center', zIndex: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem', display: 'block' }}>
                            خصم
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
                            {purchase.discount_type === 'percent' 
                              ? `${purchase.discount_value}٪` 
                              : `${purchase.discount_value}د.ع`}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            position: 'absolute',
                            width: '200%',
                            height: '200%',
                            background: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
                            backgroundSize: '15px 15px',
                            transform: 'rotate(45deg)',
                            opacity: 0.3
                          }}
                        />
                      </Box>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="h6" gutterBottom sx={{ 
                          fontWeight: 600,
                          fontSize: '0.95rem',
                          lineHeight: 1.4,
                          mb: 1
                        }}>
                          كوبون خصم {purchase.discount_type === 'percent' ? `${purchase.discount_value}٪` : `${purchase.discount_value} دينار`}
                        </Typography>
                        <Chip
                          label={purchase.redeemed ? 'تم الاستخدام' : 'لم يستخدم بعد'}
                          color={purchase.redeemed ? 'default' : 'success'}
                          size="small"
                          sx={{ 
                            mb: 1.5,
                            fontSize: '0.7rem',
                            height: 22,
                            fontWeight: 500
                          }}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                          تاريخ الشراء: {new Date(purchase.purchase_date).toLocaleDateString('ar-SA')}
                        </Typography>
                        <Paper 
                          elevation={0} 
                          sx={{ 
                            p: 1, 
                            bgcolor: 'grey.50', 
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'grey.200'
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.7rem' }}>
                            كود الكوبون:
                          </Typography>
                          <Typography variant="body2" color="primary.main" sx={{ 
                            fontWeight: 700,
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            letterSpacing: 1,
                            userSelect: 'all'
                          }}>
                            {purchase.coupon_code}
                          </Typography>
                        </Paper>
                        {purchase.expires_at && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
                            ينتهي: {new Date(purchase.expires_at).toLocaleDateString('ar-SA')}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      {/* Gift Details Modal */}
      <Dialog 
        open={modalOpen} 
        onClose={closeModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider'
          }
        }}
      >
        {selectedGift && (
          <>
            <DialogTitle sx={{ 
              position: 'relative', 
              pb: 1, 
              textAlign: 'center',
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}>
              <IconButton
                onClick={closeModal}
                sx={{ 
                  position: 'absolute', 
                  right: 8, 
                  top: 8,
                  color: 'text.secondary'
                }}
              >
                <CloseIcon />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 600, mt: 1 }}>
                تفاصيل الهدية
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ textAlign: 'center', pt: 3 }}>
              <img
                src={selectedGift.image ? `https://al-sadik.com/uploads/${selectedGift.image}` : "/gift-placeholder.png"}
                alt={selectedGift.name}
                style={{ 
                  width: '100%', 
                  maxWidth: 300, 
                  height: 200, 
                  objectFit: 'cover', 
                  borderRadius: 12,
                  marginBottom: 16,
                  border: '1px solid #f0f0f0'
                }}
              />
              <Typography variant="h5" gutterBottom sx={{ 
                fontWeight: 600,
                fontSize: '1.375rem',
                mb: 1
              }}>
                {selectedGift.name}
              </Typography>
              {selectedGift.description && (
                <Typography variant="body1" color="text.secondary" sx={{ 
                  mb: 3,
                  lineHeight: 1.6
                }}>
                  {selectedGift.description}
                </Typography>
              )}
              
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2.5, 
                  mb: 2, 
                  bgcolor: 'primary.50',
                  border: '1px solid',
                  borderColor: 'primary.100',
                  borderRadius: 1.5
                }}
              >
                {selectedGift.type === 'coupon' ? (
                  /* Coupon Details */
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        النقاط المطلوبة:
                      </Typography>
                      <Chip
                        icon={<StarIcon sx={{ fontSize: '1rem' }} />}
                        label={`${selectedGift.points_cost} نقطة`}
                        color="primary"
                        sx={{ 
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          height: 32
                        }}
                      />
                    </Stack>
                    <Divider />
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        قيمة الخصم:
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {selectedGift.discount_type === 'percent' 
                          ? `${selectedGift.discount_value}٪` 
                          : `${selectedGift.discount_value} دينار`}
                      </Typography>
                    </Box>
                  </Stack>
                ) : (
                  /* Gift Details */
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        النقاط المطلوبة لكل قطعة:
                      </Typography>
                      <Chip
                        icon={<StarIcon sx={{ fontSize: '1rem' }} />}
                        label={`${selectedGift.points_cost} نقطة`}
                        color="primary"
                        sx={{ 
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          height: 32
                        }}
                      />
                    </Stack>
                    
                    {/* Quantity Selector */}
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                        الكمية:
                      </Typography>
                      <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
                        <IconButton 
                          onClick={() => setQuantity(Math.max(1, quantity - 1))}
                          disabled={quantity <= 1}
                          sx={{ 
                            border: '1px solid',
                            borderColor: 'primary.200',
                            width: 40,
                            height: 40
                          }}
                        >
                          <RemoveIcon />
                        </IconButton>
                        
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            minWidth: 40, 
                            textAlign: 'center',
                            fontWeight: 600,
                            fontSize: '1.25rem'
                          }}
                        >
                          {quantity}
                        </Typography>
                        
                        <IconButton 
                          onClick={() => setQuantity(Math.min(10, quantity + 1))}
                          disabled={quantity >= 10 || (user?.points || 0) < (selectedGift.points_cost * (quantity + 1))}
                          sx={{ 
                            border: '1px solid',
                            borderColor: 'primary.200',
                            width: 40,
                            height: 40
                          }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                        الحد الأقصى: 10 قطع
                      </Typography>
                    </Box>

                    {/* Total Cost */}
                    <Divider />
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        إجمالي النقاط المطلوبة:
                      </Typography>
                      <Chip
                        label={`${selectedGift.points_cost * quantity} نقطة`}
                        color="primary"
                        variant="filled"
                        sx={{ 
                          fontWeight: 700,
                          fontSize: '1rem',
                          height: 36
                        }}
                      />
                    </Stack>
                  </Stack>
                )}
              </Paper>

              <Alert 
                severity="info" 
                icon={<ShippingIcon />}
                sx={{ 
                  mb: 2, 
                  textAlign: 'right',
                  bgcolor: 'info.50',
                  border: '1px solid',
                  borderColor: 'info.200',
                  '& .MuiAlert-message': {
                    width: '100%'
                  }
                }}
              >
                {selectedGift.type === 'coupon' ? (
                  <>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      • ستحصل على كود الكوبون فوراً بعد الشراء
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      • يمكنك استخدام الكوبون في أي عملية شراء
                    </Typography>
                    <Typography variant="body2">
                      • الكوبون غير قابل للإرجاع بعد الشراء
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      • سيتم إضافة الهدية إلى سلة التسوق بعد الشراء
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      • رسوم التوصيل <strong>5000</strong> دينار عراقي تُضاف عند التسليم
                    </Typography>
                    <Typography variant="body2">
                      • ستحصل على رمز هدية فريد للمتابعة
                    </Typography>
                  </>
                )}
              </Alert>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 0, gap: 1 }}>
              <Button 
                onClick={closeModal} 
                variant="outlined" 
                sx={{ 
                  flex: 1,
                  py: 1.25,
                  fontWeight: 500,
                  textTransform: 'none',
                  borderRadius: 1.5
                }}
              >
                إلغاء
              </Button>
              <Button
                onClick={() => selectedGift.type === 'coupon' ? purchaseCoupon(selectedGift) : addGiftToCart(selectedGift, quantity)}
                variant="contained"
                disabled={
                  !user?.points || 
                  user.points < (selectedGift.type === 'coupon' ? selectedGift.points_cost : (selectedGift.points_cost * quantity))
                }
                sx={{ 
                  flex: 1,
                  py: 1.25,
                  fontWeight: 500,
                  textTransform: 'none',
                  borderRadius: 1.5
                }}
              >
                {!user?.points || user.points < (selectedGift.type === 'coupon' ? selectedGift.points_cost : (selectedGift.points_cost * quantity)) ? (
                  'نقاط غير كافية'
                ) : selectedGift.type === 'coupon' ? (
                  'شراء الكوبون'
                ) : (
                  `إضافة ${quantity} ${quantity === 1 ? 'هدية' : 'هدايا'} للسلة`
                )}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbarSeverity} 
          onClose={() => setSnackbarOpen(false)}
          sx={{ minWidth: 300 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Add to Cart Success Dialog */}
      <Dialog
        open={!!justPurchased}
        onClose={() => setJustPurchased(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'primary.200'
          }
        }}
      >
        {justPurchased && (
          <>
            <DialogTitle sx={{ 
              textAlign: 'center',
              bgcolor: 'primary.50',
              borderBottom: '1px solid',
              borderColor: 'primary.200',
              color: 'primary.dark'
            }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                {justPurchased.coupon_code ? '🎉 تم شراء الكوبون!' : '🛒 تمت الإضافة للسلة!'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {justPurchased.coupon_code 
                  ? 'تم خصم النقاط بنجاح وإليك كود الكوبون' 
                  : 'تم إضافة الهدايا إلى سلة التسوق بنجاح'}
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              {justPurchased.coupon_code ? (
                /* Coupon Purchase Success */
                <>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      {justPurchased.coupon.name}
                    </Typography>
                  </Box>

                  <Paper elevation={0} sx={{ p: 3, bgcolor: 'success.50', borderRadius: 2, mb: 2, border: '2px solid', borderColor: 'success.200' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'center' }}>
                      كود الكوبون الخاص بك:
                    </Typography>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 700, 
                      color: 'success.dark',
                      textAlign: 'center',
                      fontFamily: 'monospace',
                      letterSpacing: 2,
                      userSelect: 'all',
                      cursor: 'pointer'
                    }}>
                      {justPurchased.coupon_code}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                      انقر لتحديد ونسخ الكود
                    </Typography>
                  </Paper>

                  <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1.5, mb: 2 }}>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          النقاط المخصومة:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {justPurchased.points_cost} نقطة
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          نقاطك المتبقية:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          {justPurchased.remaining_points} نقطة
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>

                  <Alert severity="success" sx={{ mb: 2 }}>
                    احتفظ بكود الكوبون في مكان آمن. يمكنك استخدامه في أي عملية شراء من المتجر.
                  </Alert>
                </>
              ) : (
                /* Gift Add to Cart Success */
                <>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <img
                      src={justPurchased.gift.image ? `https://al-sadik.com/uploads/${justPurchased.gift.image}` : "/gift-placeholder.png"}
                      alt={justPurchased.gift.name}
                      style={{ 
                        width: 120, 
                        height: 120, 
                        objectFit: 'cover', 
                        borderRadius: 12,
                        border: '2px solid #1677ff'
                      }}
                    />
                    <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>
                      {justPurchased.gift.name}
                    </Typography>
                  </Box>

                  <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1.5, mb: 2 }}>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          الكمية المضافة:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {justPurchased.quantity} {justPurchased.quantity === 1 ? 'هدية' : 'هدايا'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          التكلفة الإجمالية:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {justPurchased.totalCost} نقطة
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          نقاطك الحالية:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          {user?.points || 0} نقطة
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>

                  <Alert severity="info" sx={{ mb: 2 }}>
                    تم إضافة {justPurchased.quantity} {justPurchased.quantity === 1 ? 'هدية' : 'هدايا'} إلى سلة التسوق. 
                    سيتم خصم النقاط عند إتمام عملية الشراء. 
                    يمكنك الآن إتمام الطلب أو متابعة التسوق.
                  </Alert>
                </>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 0, gap: 1 }}>
              <Button 
                onClick={() => setJustPurchased(null)}
                variant="outlined" 
                sx={{ 
                  flex: 1,
                  py: 1.25,
                  fontWeight: 500,
                  textTransform: 'none',
                  borderRadius: 1.5
                }}
              >
                {justPurchased.coupon_code ? 'إغلاق' : 'متابعة التسوق'}
              </Button>
              {!justPurchased.coupon_code && (
                <Button
                  onClick={() => {
                    setJustPurchased(null);
                    navigate('/store'); // Go to store to view cart
                  }}
                  variant="contained"
                  sx={{ 
                    flex: 1,
                    py: 1.25,
                    fontWeight: 500,
                    textTransform: 'none',
                    borderRadius: 1.5,
                    background: 'linear-gradient(135deg, #1677ff 0%, #4096ff 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #0958d9 0%, #1677ff 100%)'
                    }
                  }}
                >
                  عرض السلة
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
}
